/* ==========================================================================
   Scapevo — theme JavaScript
   Vanilla custom elements, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  const routes = (window.Scapevo && window.Scapevo.routes) || {};
  const strings = (window.Scapevo && window.Scapevo.strings) || {};

  /* --- Helpers ----------------------------------------------------------- */
  function announce(message) {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  function formatMoney(cents) {
    const format = (window.Scapevo && window.Scapevo.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, opener) {
    const focusables = () => Array.from(container.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
    const first = focusables()[0];
    if (first) first.focus();

    function handler(event) {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }

    container.__focusHandler = handler;
    container.__opener = opener;
    document.addEventListener('keydown', handler);
  }

  function releaseFocus(container) {
    if (container.__focusHandler) {
      document.removeEventListener('keydown', container.__focusHandler);
      container.__focusHandler = null;
    }
    if (container.__opener && typeof container.__opener.focus === 'function') {
      container.__opener.focus();
      container.__opener = null;
    }
  }

  async function fetchSections(sectionIds, path) {
    // Sections can only be re-rendered from a URL that actually renders them,
    // so callers on the cart page must pass their own path.
    const base = path || routes.root || '/';
    const response = await fetch(`${base}?sections=${sectionIds.join(',')}`.replace('//?', '/?'));
    return response.json();
  }

  function replaceSectionHtml(html, selector) {
    const target = document.querySelector(selector);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(selector);
    if (source) target.innerHTML = source.innerHTML;
  }

  function updateCartCounts(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }

  /* --- Drawer base ------------------------------------------------------- */
  class DrawerElement extends HTMLElement {
    constructor() {
      super();
      this.overlay = null;
    }

    connectedCallback() {
      this.drawer = this.querySelector('[data-drawer]');
      this.overlay = this.querySelector('[data-overlay]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target.hasAttribute('data-overlay')) {
          this.close();
        }
      });
      this.escHandler = (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      };
    }

    get isOpen() {
      return this.drawer && this.drawer.hasAttribute('data-open');
    }

    open(opener) {
      if (!this.drawer) return;
      this.drawer.setAttribute('data-open', '');
      if (this.overlay) this.overlay.setAttribute('data-open', '');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.escHandler);
      window.setTimeout(() => trapFocus(this.drawer, opener), 80);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      if (!this.drawer) return;
      this.drawer.removeAttribute('data-open');
      if (this.overlay) this.overlay.removeAttribute('data-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.escHandler);
      releaseFocus(this.drawer);
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }

  /* --- Cart drawer ------------------------------------------------------- */
  class CartDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      window.cartDrawer = this;
    }

    async refresh() {
      try {
        const sections = await fetchSections(['cart-drawer', 'header']);
        if (sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const newInner = parsed.querySelector('[data-cart-drawer-inner]');
          const currentInner = this.querySelector('[data-cart-drawer-inner]');
          if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
        }
        if (sections.header) {
          replaceSectionHtml(sections.header, '.header__actions');
        }
      } catch (error) {
        console.error('[Scapevo] Cart refresh failed', error);
      }
    }
  }

  /* --- Menu / search drawers --------------------------------------------- */
  class MenuDrawer extends DrawerElement {}
  class SearchDrawer extends DrawerElement {
    open(opener) {
      super.open(opener);
      const input = this.querySelector('input[type="search"]');
      if (input) window.setTimeout(() => input.focus(), 200);
    }
  }

  /* --- Global drawer triggers -------------------------------------------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const targetId = trigger.getAttribute('data-drawer-open');
    const drawer = document.getElementById(targetId);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* --- Quantity input ---------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  /* --- Product form (AJAX add to cart) ----------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      if (this.dataset.ajax === 'false') return;
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      const formData = new FormData(this.form);
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData
        });
        const data = await response.json();

        if (data.status) {
          this.showError(data.description || data.message);
          return;
        }

        announce(strings.addedToCart || 'Added to cart');
        document.dispatchEvent(new CustomEvent('cart:updated'));

        if (window.Scapevo.cartType === 'drawer' && window.cartDrawer) {
          await window.cartDrawer.refresh();
          window.cartDrawer.open(this.submitButton);
        } else {
          window.location.href = routes.cart_url;
        }
      } catch (error) {
        this.showError('Something went wrong. Please try again.');
        console.error('[Scapevo] Add to cart failed', error);
      } finally {
        this.setLoading(false);
      }
    }

    showError(message) {
      if (!this.errorEl) {
        announce(message);
        return;
      }
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    setLoading(state) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', state);
      this.submitButton.disabled = state;
      const spinner = this.submitButton.querySelector('.loading-spinner');
      if (spinner) spinner.hidden = !state;
    }
  }

  /* --- Variant selection -------------------------------------------------- */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.variants = this.getVariantData();
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.updateAvailability();
      this.updateColorMedia(this.selectedColor);
    }

    getVariantData() {
      const script = this.querySelector('[data-variant-json]');
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((el) => el.value);
    }

    get selectedColor() {
      const control = Array.from(
        this.querySelectorAll('input[type="radio"]:checked, select')
      ).find((el) => this.isColorOption(el.dataset.optionName));
      return control ? control.value : '';
    }

    isColorOption(name) {
      return ['color', 'colour', 'finish', '颜色'].includes((name || '').trim().toLowerCase());
    }

    onVariantChange(event) {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((option, index) => option === options[index]));

      this.updateOptionLabels();
      this.updateAvailability();

      if (event.target && this.isColorOption(event.target.dataset.optionName)) {
        this.updateColorMedia(event.target.value);
      }

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateFormId(variant);
      this.updatePrice(variant);
      if (!this.selectedColor) this.updateMedia(variant);
      this.updateButton(variant);
      this.updateInventory(variant);
      this.updateSku(variant);
    }

    updateOptionLabels() {
      this.querySelectorAll('.variant-option').forEach((group) => {
        const display = group.querySelector('[data-option-value]');
        if (!display) return;
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        display.textContent = checked ? checked.value : select ? select.value : '';
      });
    }

    updateAvailability() {
      const inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return;
      const current = this.selectedOptions;

      inputs.forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10);
        const candidate = current.slice();
        candidate[index] = input.value;
        const match = this.variants.find((v) => v.options.every((option, i) => option === candidate[i]));
        const available = Boolean(match && match.available);
        input.disabled = !available;
      });
    }

    setUnavailable() {
      const button = document.querySelector(`#ProductSubmit-${this.sectionId}`);
      if (!button) return;
      const text = button.querySelector('[data-submit-text]');
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
      if (text) text.textContent = strings.unavailable || 'Unavailable';
    }

    updateUrl(variant) {
      if (!variant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }

    updateFormId(variant) {
      document.querySelectorAll(`#ProductForm-${this.sectionId} [name="id"]`).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePrice(variant) {
      const container = document.querySelector(`#Price-${this.sectionId}`);
      if (!container) return;
      const regular = container.querySelector('[data-price-regular]');
      const compare = container.querySelector('[data-price-compare]');
      if (regular) regular.textContent = formatMoney(variant.price);
      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.textContent = formatMoney(variant.compare_at_price);
          compare.hidden = false;
          container.classList.add('price--on-sale');
        } else {
          compare.hidden = true;
          container.classList.remove('price--on-sale');
        }
      }
    }

    updateSku(variant) {
      const sku = document.querySelector(`#Sku-${this.sectionId}`);
      if (!sku) return;
      sku.textContent = variant.sku || '';
      sku.hidden = !variant.sku;
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const target = document.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (!target) return;
      const gallery = target.closest('[data-product-media]');
      if (gallery && gallery.scrollWidth > gallery.clientWidth) {
        gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    updateColorMedia(color) {
      if (!color) return;
      const normalizedColor = color.trim().toLowerCase();
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      const scope = section || document;
      const target = Array.from(scope.querySelectorAll('[data-media-color]')).find(
        (media) => media.dataset.mediaColor.trim().toLowerCase() === normalizedColor
      );
      if (!target) return;

      const gallery = target.closest('[data-product-media]');
      if (!gallery) return;

      if (gallery.dataset.layout === 'grid') {
        gallery.prepend(target);
      } else {
        gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
      }

      scope.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.mediaThumb === target.dataset.mediaId));
      });
    }

    updateButton(variant) {
      const button = document.querySelector(`#ProductSubmit-${this.sectionId}`);
      if (!button) return;
      const text = button.querySelector('[data-submit-text]');
      if (variant.available) {
        button.removeAttribute('aria-disabled');
        button.disabled = false;
        if (text) text.textContent = strings.addToCart || 'Add to cart';
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        if (text) text.textContent = strings.soldOut || 'Sold out';
      }
    }

    updateInventory(variant) {
      const el = document.querySelector(`#Inventory-${this.sectionId}`);
      if (!el) return;
      const data = el.dataset;
      el.classList.remove('inventory--low', 'inventory--out');
      if (!variant.available) {
        el.textContent = data.outText || '';
        el.classList.add('inventory--out');
        return;
      }
      const qty = variant.inventory_quantity;
      const threshold = parseInt(data.threshold || '5', 10);
      if (variant.inventory_management && qty > 0 && qty <= threshold) {
        el.textContent = (data.lowText || '').replace('{{ count }}', qty);
        el.classList.add('inventory--low');
      } else {
        el.textContent = data.inText || '';
      }
    }
  }

  /* --- Cart items (quantity change / remove) ------------------------------ */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (!input) return;
        this.updateQuantity(input.dataset.line, input.value);
      });

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-item]');
        if (!remove) return;
        event.preventDefault();
        this.updateQuantity(remove.dataset.line, 0);
      });
    }

    async updateQuantity(line, quantity) {
      this.classList.add('is-loading');
      try {
        const response = await fetch(routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
        });
        const cart = await response.json();
        updateCartCounts(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

        if (this.closest('cart-drawer') && window.cartDrawer) {
          await window.cartDrawer.refresh();
        } else {
          const sections = await fetchSections(['main-cart', 'header'], window.location.pathname);
          if (sections['main-cart']) replaceSectionHtml(sections['main-cart'], '[data-cart-root]');
          if (sections.header) replaceSectionHtml(sections.header, '.header__actions');
        }
      } catch (error) {
        console.error('[Scapevo] Cart update failed', error);
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  /* --- Cart note ---------------------------------------------------------- */
  document.addEventListener(
    'change',
    debounce((event) => {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      fetch(routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }, 400)
  );

  /* --- Quick add ---------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    const variantId = button.dataset.quickAdd;
    if (!variantId) return;

    button.classList.add('is-loading');
    button.disabled = true;
    try {
      const response = await fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description);
      announce(strings.addedToCart || 'Added to cart');
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (window.Scapevo.cartType === 'drawer' && window.cartDrawer) {
        await window.cartDrawer.refresh();
        window.cartDrawer.open(button);
      } else {
        window.location.href = routes.cart_url;
      }
    } catch (error) {
      console.error('[Scapevo] Quick add failed', error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  });

  /* --- Predictive search --------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(this.onInput.bind(this), 250));
      this.input.setAttribute('autocomplete', 'off');
    }

    async onInput() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product,collection,page,article',
          'resources[limit]': '6',
          'section_id': 'predictive-search'
        });
        const response = await fetch(`${routes.predictive_search_url}?${params}`);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const content = parsed.querySelector('[data-predictive-inner]');
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (error) {
        console.error('[Scapevo] Predictive search failed', error);
      }
    }
  }

  /* --- Slider ------------------------------------------------------------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.slider) return;

      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));
      this.slider.addEventListener('scroll', debounce(() => this.updateButtons(), 100));
      this.updateButtons();
    }

    get step() {
      const first = this.slider.firstElementChild;
      if (!first) return 300;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap || '16');
      return first.getBoundingClientRect().width + gap;
    }

    scrollBy(direction) {
      this.slider.scrollBy({ left: this.step * direction, behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.prev || !this.next) return;
      const max = this.slider.scrollWidth - this.slider.clientWidth - 2;
      this.prev.disabled = this.slider.scrollLeft <= 2;
      this.next.disabled = this.slider.scrollLeft >= max;
    }
  }

  /* --- Product media thumbs ------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-media-thumb]');
    if (!thumb) return;
    event.preventDefault();
    const gallery = document.querySelector('[data-product-media]');
    const target = document.querySelector(`[data-media-id="${thumb.dataset.mediaThumb}"]`);
    if (!target) return;
    thumb.parentElement.querySelectorAll('[data-media-thumb]').forEach((el) => el.setAttribute('aria-current', 'false'));
    thumb.setAttribute('aria-current', 'true');
    if (gallery && gallery.scrollWidth > gallery.clientWidth) {
      gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
    } else {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  /* --- Desktop nav dropdowns ---------------------------------------------- */
  function initNav() {
    const items = document.querySelectorAll('[data-nav-item]');
    items.forEach((item) => {
      const trigger = item.querySelector('[data-nav-trigger]');
      if (!trigger) return;
      let timer;

      const open = () => {
        clearTimeout(timer);
        items.forEach((other) => {
          if (other !== item) other.removeAttribute('data-open');
        });
        item.setAttribute('data-open', '');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        timer = setTimeout(() => {
          item.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
        }, 120);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close();
      });
      trigger.addEventListener('click', (event) => {
        if (trigger.tagName === 'BUTTON') {
          event.preventDefault();
          item.hasAttribute('data-open') ? close() : open();
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      items.forEach((item) => item.removeAttribute('data-open'));
    });
  }

  /* --- Sticky header shadow ------------------------------------------------ */
  function initStickyHeader() {
    const header = document.querySelector('[data-header-wrapper]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- Announcement rotator ------------------------------------------------ */
  function initAnnouncement() {
    document.querySelectorAll('[data-announcement]').forEach((bar) => {
      const slides = bar.querySelectorAll('.announcement__slide');
      if (slides.length < 2) return;
      const delay = parseInt(bar.dataset.announcement, 10) || 5000;
      let index = 0;
      setInterval(() => {
        slides[index].removeAttribute('data-active');
        index = (index + 1) % slides.length;
        slides[index].setAttribute('data-active', '');
      }, delay);
    });
  }

  /* --- Reveal on scroll ---------------------------------------------------- */
  function initReveal() {
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    targets.forEach((el) => observer.observe(el));
  }

  /* --- Facet form (auto submit) -------------------------------------------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('change', debounce(() => this.submit(), 300));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
    }

    async submit() {
      const params = new URLSearchParams(new FormData(this.form)).toString();
      const url = `${window.location.pathname}?${params}`;
      this.classList.add('is-loading');
      try {
        const response = await fetch(url);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        ['[data-product-grid-container]', '[data-facets-container]', '[data-active-facets]'].forEach((selector) => {
          const source = parsed.querySelector(selector);
          const target = document.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
        });
        window.history.pushState({ path: url }, '', url);
        const grid = document.querySelector('[data-product-grid-container]');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  /* --- Sort select --------------------------------------------------------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-by]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* --- Copy link ----------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-copy-link]');
    if (!button) return;
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(button.dataset.copyLink || window.location.href);
      const label = button.querySelector('[data-copy-label]') || button;
      const original = label.textContent;
      label.textContent = strings.copied || 'Link copied';
      setTimeout(() => {
        label.textContent = original;
      }, 2000);
    } catch (error) {
      console.error('[Scapevo] Copy failed', error);
    }
  });

  /* --- Newsletter offer popup -------------------------------------------- */
  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-newsletter-dialog]');
      this.hasSuccess = Boolean(this.querySelector('[data-newsletter-success]'));
      this.hasError = Boolean(this.querySelector('[data-newsletter-error]'));
      this.delay = parseInt(this.dataset.delay || '6000', 10);
      this.dismissDays = parseInt(this.dataset.dismissDays || '7', 10);
      this.storageKey = this.dataset.storageKey || 'scapevo-newsletter-popup';
      this.escHandler = (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      };

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-newsletter-close]')) this.close();
        if (event.target.closest('[data-newsletter-open]')) this.open();
        if (event.target.closest('[data-newsletter-dismiss]')) this.dismissCollapsed();

        const copyButton = event.target.closest('[data-newsletter-copy]');
        if (copyButton) this.copyCode(copyButton);
      });

      const dismissal = this.getDismissal();
      if (this.hasSuccess || this.hasError) {
        window.setTimeout(() => this.open(), 80);
      } else if (!dismissal.active) {
        this.openTimer = window.setTimeout(() => this.open(), this.delay);
      } else if (!dismissal.subscribed && !dismissal.launcherHidden) {
        this.collapse(true);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      document.removeEventListener('keydown', this.escHandler);
    }

    get isOpen() {
      return !this.hidden && this.classList.contains('is-visible');
    }

    getDismissal() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (!stored) return { active: false, subscribed: false, launcherHidden: false };

        const legacyExpiry = Number(stored);
        if (Number.isFinite(legacyExpiry)) {
          return { active: legacyExpiry > Date.now(), subscribed: false, launcherHidden: false };
        }

        const dismissal = JSON.parse(stored);
        return {
          active: Number(dismissal.until) > Date.now(),
          subscribed: Boolean(dismissal.subscribed),
          launcherHidden: Boolean(dismissal.launcherHidden)
        };
      } catch (error) {
        return { active: false, subscribed: false, launcherHidden: false };
      }
    }

    rememberDismissal(launcherHidden) {
      const days = this.hasSuccess ? 365 : this.dismissDays;
      try {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            until: Date.now() + days * 86400000,
            subscribed: this.hasSuccess,
            launcherHidden: Boolean(launcherHidden)
          })
        );
      } catch (error) {
        // The popup can still close when storage is unavailable.
      }
    }

    open() {
      window.clearTimeout(this.openTimer);
      this.hidden = false;
      this.classList.remove('is-collapsed');
      if (this.dialog) this.dialog.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.escHandler);
      window.requestAnimationFrame(() => {
        this.classList.add('is-visible');
        window.setTimeout(() => trapFocus(this.dialog), 80);
      });
    }

    close() {
      if (!this.isOpen) return;
      this.rememberDismissal();
      this.classList.remove('is-visible');
      if (this.dialog) this.dialog.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.escHandler);
      releaseFocus(this.dialog);
      if (this.hasSuccess) {
        window.setTimeout(() => {
          this.hidden = true;
        }, 230);
      } else {
        this.collapse();
      }
    }

    collapse(immediate) {
      const finish = () => {
        this.hidden = false;
        this.classList.add('is-collapsed');
      };
      if (immediate) finish();
      else window.setTimeout(finish, 230);
    }

    dismissCollapsed() {
      if (!this.classList.contains('is-collapsed')) return;
      this.rememberDismissal(true);
      this.classList.remove('is-collapsed');
      this.hidden = true;
    }

    async copyCode(button) {
      const code = button.dataset.newsletterCopy;
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const input = document.createElement('input');
        input.value = code;
        input.setAttribute('readonly', '');
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      const original = button.textContent;
      button.textContent = 'Copied!';
      announce(`${code} copied`);
      window.setTimeout(() => {
        button.textContent = original;
      }, 1800);
    }
  }

  /* --- Register ------------------------------------------------------------ */
  const definitions = {
    'cart-drawer': CartDrawer,
    'menu-drawer': MenuDrawer,
    'search-drawer': SearchDrawer,
    'quantity-input': QuantityInput,
    'product-form': ProductForm,
    'variant-selects': VariantSelects,
    'cart-items': CartItems,
    'predictive-search': PredictiveSearch,
    'slider-component': SliderComponent,
    'facet-form': FacetForm,
    'newsletter-popup': NewsletterPopup
  };

  Object.keys(definitions).forEach((name) => {
    if (!customElements.get(name)) customElements.define(name, definitions[name]);
  });

  function init() {
    initNav();
    initStickyHeader();
    initAnnouncement();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after Shopify section reloads in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
